import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';

import { IconComponent } from '@shared/components';
import { EllipsisTooltipDirective } from '@shared/directives/tooltip';

import { NavItemComponent } from './nav-item.component';

import type { NavigationSection } from '../models/navigation.model';

/**
 * Navigation section component.
 * Renders a collapsible section with child items.
 * When collapsed, shows only the section icon (peek reveals labels via sidebar hover).
 */
@Component({
  selector: 'app-nav-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NavItemComponent, IconComponent, TranslocoPipe, EllipsisTooltipDirective],
  host: {
    class: 'nav-section block',
    'data-testid': 'nav-section',
    '[attr.data-state]': 'expanded() ? "open" : "closed"',
  },
  template: `
    @if (collapsed()) {
      <button
        type="button"
        class="nav-section-trigger justify-center"
      >
        <app-icon [name]="section().icon" />
      </button>
    } @else {
      <button
        type="button"
        class="nav-section-trigger"
        [attr.aria-expanded]="expanded()"
        [attr.aria-controls]="'section-' + section().id"
        (click)="toggleSection.emit()"
      >
        <app-icon [name]="section().icon" />
        <span class="truncate" appEllipsisTooltip [contrasted]="true">{{ section().label | transloco }}</span>
        <app-icon name="chevron-down" class="nav-section-chevron" />
      </button>

      <div
        [id]="'section-' + section().id"
        class="nav-collapsible"
        [attr.data-state]="expanded() ? 'open' : 'closed'"
      >
        <ul class="nav-children" role="list">
          @for (item of section().children; track item.id) {
            <li>
              <app-nav-item [item]="item" [currentUrl]="currentUrl()" />
            </li>
          } @empty {
            <!-- Section has no children -->
          }
        </ul>
      </div>
    }
  `,
})
export class NavSectionComponent {
  /** Section data - accepts any entry with children (groups or sections) */
  readonly section = input.required<NavigationSection>();

  /** Whether sidebar is collapsed */
  readonly collapsed = input.required<boolean>();

  /** Whether this section is expanded */
  readonly expanded = input.required<boolean>();

  /** Current URL for active state detection */
  readonly currentUrl = input.required<string>();

  /** Emitted when section toggle is clicked */
  readonly toggleSection = output();
}
